/**
 * ? Api Lists ?
 */
import type { CodeType } from '@/types/enums'
import type { ApiRes } from '@/uTypes/api'
import type { PatchUserPayload } from '@/uTypes/user/account'
import type { GetVoucherResponse } from '@/uTypes/voucher'
import type { AccountTerm } from '@/uTypes/_common/public'

export const useApi = () => {
  const http = useHttp()
  const httpV2 = useHttp('v2') // api v2 호출시 사용
  const httpV3 = useHttp('v3') // api v2 호출시 사용

  const { getPlanNameQuery, getPlanIdQuery, getEventReplyQuery, getOrderTypeQuery } = useQuery()

  /***************************************************************************
   * 통합계정
   ***************************************************************************/
  // 회원/비회원 구매 조건 확인
  const checkEligibilityCondition = (params: { periodType: 'within'; days: number }) => {
    return http.post('/account/eligibility', params)
  }
  // 통합계정 기준 회원데이터 조회
  const getUserAccountInfo = () => {
    return http.get('/account')
  }

  const signUpUser = (params: any) => {
    return http.post('/users/signup', params)
  }
  const loginUsimsaUser = (params: any) => {
    return httpV2.post('/auth/login', params)
  }
  const loginSimpleUser = (userId: string) => {
    const { navigate } = useCommon()
    const partnersKey = String(useRoute().params.partner)
    if (!userId) {
      navigate('/user', partnersKey)
      return
    }
    return http.get(`/auth/login/${userId}`)
  }
  const logoutUser = () => {
    return http.post('/auth/logout', null)
  }
  const patchUserInformation = (payload: PatchUserPayload): Promise<ApiRes> => {
    return http.patch('/users', payload)
  }
  const searchUserId = (params: any) => {
    return http.post('/users/account', params)
  }
  const searchUserPassword = (params: any) => {
    return http.post('/users/password', params)
  }
  const validateUserPasswordReset = (payload: { userId: string; mobile: string }) => {
    return http.post('users/validate/password-reset', payload)
  }
  const patchUserPassword = (params: any) => {
    return http.patch('/users/password', params)
  }
  const patchLogoutUserPassword = (params: any) => {
    return http.patch('/users/password/non-member', params)
  }
  const getUserPurchaseAlarm = () => {
    return http.get('/users/alarm/count')
  }
  const patchUserPurchaseAlarm = () => {
    return http.rawPatch('/users/alarm')
  }
  const deleteUser = () => {
    return http.put('/account')
  }
  const getAccountTerms = (): Promise<{ success: boolean; data: AccountTerm[] }> => {
    return http.get('/account/terms')
  }
  const getUserMileage = () => {
    return http.get('/users/mile')
  }
  const getAvaliablePlan = (planId: string | undefined) => {
    return http.get(`/plans/${planId ?? ''}/available`)
  }
  const getPlanOption = (planId: string) => {
    return http.get(`/plans/${planId}/options`)
  }
  const getAllCoupon = () => {
    return http.get('/coupons', useCouponScope().getPartnersKeyHeader())
  }
  const registerCoupon = (couponId: string) => {
    return http.rawPost(`/coupons/${couponId}`)
  }
  const registerCouponByUser = (number: string) => {
    return http.rawPost(`/coupons/${number}/user`, useCouponScope().getPartnersKeyHeader())
  }
  const registerPublicCouponById = (couponId: string) => {
    return http.rawPost(
      `/coupons/${couponId}/user/by-id`,
      useCouponScope().getPartnersKeyHeader()
    )
  }
  const getAllPoint = () => {
    return httpV2.get('/points')
  }
  const getOrderCode = () => {
    return http.get('/orders')
  }
  const getOrderExpiredList = (page: number, count: number) => {
    return http.get(`/orders/user/expired/${page}/${count}`)
  }
  const getOrderHistoryList = (page: number, count: number) => {
    return http.get(`/orders/user/history/${page}/${count}`)
  }

  const getUserOrder = (orderId: string) => {
    return useHttp().get(`/users/orders/${orderId}`)
  }
  const getUserOrderItem = (subOrderId: string) => {
    return useHttp().get(`/users/orders/items/${subOrderId}`)
  }
  const getUserMultiPrdOrderItem = (subOrderId: string, type: 'group' | 'order') => {
    return httpV3.get(`/users/orders/items/${subOrderId}${getOrderTypeQuery(type)}`)
  }
  const getUserOrderSim = (orderId: string) => {
    return useHttp().get(`/users/orders/${orderId}/sim`)
  }
  const createPurchaseOrder = (hash: string, params: any) => {
    return http.post(`/orders/${hash}`, params)
  }
  const createGuestPurchaseOrder = (hash: string, params: any) => {
    return http.post(`/orders/${hash}/guest`, params)
  }
  const refundOrder = (params: any) => {
    return http.post('/orders/cancel', params)
  }
  const refundChargeOrder = (params: any) => {
    return http.post('/orders/item/cancel', params)
  }
  const deleteESimInfo = (orderItemId: string) => {
    return httpV2.del('/orders/user/' + orderItemId)
  }
  const deleteMultiESimInfo = (orderItemId: string, type: 'group' | 'order') => {
    return httpV3.del(`/orders/user/${orderItemId}${getOrderTypeQuery(type)}`)
  }
  const chargePurchaseOrder = (hash: string, params: any) => {
    return http.post(`/orders/charge/${hash}`, params)
  }
  const getCompleteCheckinInformation = (orderId: string) => {
    return http.get(`/orders/complete/checkin/${orderId}`)
  }
  const getGiftBoxVoucher = (giftBoxOrderId: string) => {
    return http.get(`/orders/gift/${giftBoxOrderId}`)
  }
  const postCheckInOrder = (orderId: string, params: any) => {
    return http.post(`/orders/confirmed/${orderId}`, params)
  }
  const getCategoryCode = (code: CodeType) => {
    return http.get(`/code/${code}`)
  }
  const getBoardAlarms = () => {
    return httpV2.get('/boards/notice')
  }
  const getBoardByNotice = (page: number, count: number) => {
    return http.get(`/boards/BDT001/${page}/${count}`)
  }
  const getBoardByNoticeDetail = (id: string) => {
    return http.get(`/boards/BDT001/${id}`)
  }
  const getBoardByFaq = () => {
    return http.get('/boards/BDT002')
  }
  const getImages = (id: string) => {
    return http.get(`/images/${id}`)
  }
  const getImageDetail = (id: string) => {
    return http.get(`/images/detail/${id}`)
  }
  const createReview = (orderId: string | string[], params: any) => {
    return http.post(`/reviews/${orderId}`, params)
  }
  const getTotalReviewList = (page: number, count: number, planName: string) => {
    return http.get(`/reviews/${page}/${count}${getPlanNameQuery(planName)}`)
  }
  const getUserReviewList = () => {
    return http.get('/reviews/user')
  }
  const patchUserReview = (reviewId: string | string[], params: any) => {
    return http.patch(`/reviews/${reviewId}`, params)
  }
  const postSmsRequest = (params: any) => {
    return http.post('/sms/auth', params)
  }
  const postSmsVerifying = (requestId: string, params: any) => {
    return http.post(`/sms/auth/${requestId}`, params)
  }
  const sendInquiryMail = (params: any) => {
    return http.post('/mail/inquiry', params)
  }
  const sendOrderMail = (orderId: string, templateId: string) => {
    return http.post(`/mail/order/${orderId}/template/${templateId}`, null)
  }
  const sendGiftOrderMail = (orderId: string, templateId: string, params: any) => {
    return http.post(`/mail/gift/order/${orderId}/template/${templateId}`, params)
  }
  const getPartnersInfo = (partner: string) => {
    return http.get(`/channels/${partner}`)
  }
  const getPartnersPlanList = (channelId: string) => {
    return http.get(`/channels/${channelId}/plans/day`)
  }
  const getPartnersUsimPlanList = (channelId: string) => {
    return http.get(`/channels/${channelId}/plans/usim`)
  }
  // const getPartnersOptionList = (channelId:string, planId:string) => {
  //   return http.get(`/channels/${channelId}/plans/day/${planId}/options`)
  // }
  const getPartnersOption = (channelId: string, simType: SimType, optionId: string) => {
    return http.get(`channels/${channelId}/${simType}/options/${optionId}`)
  }
  const enrollGuideTalk = (params: any) => {
    return http.post('/guide-talk-sends', params)
  }
  const getPlanRelCarrierApn = (planId: string) => {
    return http.get('/planrelcarrierapn' + getPlanIdQuery(planId))
  }
  const putExternalOrder = (orderId: string, iccid: string) => {
    return http.put(`/orders/${orderId}/iccid/${iccid}`)
  }
  const getAddon = (packageId: string) => {
    return http.get('/package-rel-addon/' + packageId)
  }
  const getDeliveryMethod = () => {
    return http.get('/users/delivery/method')
  }
  const patchDeliveryAddress = (params: any) => {
    return http.patch('/users/delivery/address', params)
  }
  const getChannelEsimOption = (channelId: string, planId: string) => {
    return http.get(`/channels/${channelId}/plans/day/${planId}/options`)
  }
  // const getChannelUsimOption = (channelId:string, planId:string) => {
  //   return http.get(`/channels/${channelId}/plans/${planId}/usim/options`)
  // }
  const getDailyUsage = (orderId: string, iccid: string): Promise<DailyUsageDataRes> => {
    return http.get(`supplier/order/${orderId}/iccid/${iccid}/history`)
  }
  const getActiveStatus = (orderId: string, iccid: string): Promise<ActiveInfoRes> => {
    return http.get(`/supplier/order/${orderId}/iccid/${iccid}/active`)
  }
  const getRealTimeUsage = (orderId: string, iccid: string): Promise<ApiRes<UsageData>> => {
    return http.get(`/supplier/order/${orderId}/iccid/${iccid}/usage`)
  }
  const getUsableAddon = (orderId: string, iccid: string) => {
    return http.get(`/supplier/order/${orderId}/iccid/${iccid}/addon`)
  }
  const createAddon = (hash: string, params: any) => {
    return http.post(`/orders/addon/${hash}`, params)
  }
  const getOrderUserData = async (optionId: string, simType: 'usim' | 'esim' = 'esim') => {
    return http.get(`/users?planOptionId=${optionId}&simType=${simType}`)
  }
  const getOptionDetail = (simType: 'esim' | 'usim', optionId: string) => {
    return http.get(`plans/${simType}/options/${optionId}`)
  }
  const getProductSearch = (keyword: string) => {
    return http.get(`products/search?keyword=${keyword}`)
  }
  const getAddonList = (orderId: string, iccid: string): Promise<AddonOptionRes> => {
    return httpV2.get(`supplier/order/${orderId}/iccid/${iccid}/addon`)
  }
  const getOptionsList = (planId: string): Promise<ExtendOptionRes> => {
    return httpV2.get(`plans/${planId}/options`)
  }
  const patchUserNotification = (params: any) => {
    return http.patch('/users/notification', params)
  }
  const postUserConsent = (params: any) => {
    return http.post('/users/consent', params)
  }

  /* 제휴쿠폰 관련 API */
  const collabCouponApi = {
    issue: (collabCouponId: string) => {
      return http.patch(`/partner-coupons/${collabCouponId}`, null)
    },
    check: (collabCouponId: string) => {
      return http.get(`/partner-coupons/${collabCouponId}`)
    },
    exhaust: (collabCouponId: string) => {
      return http.get(`/partner-coupons/${collabCouponId}/check-availability`)
    }
  }

  /* 혜택(이벤트) 관련 API */
  const eventApi = {
    getEventSections: () => {
      return http.get('/events/sections')
    },
    getEventDetail: (eventId: string) => {
      return httpV2.get(`/events/${eventId}`)
    },
    getBanners: (): Promise<EventListRes> => {
      return http.get('/events/main')
    },
    getEventApply: (eventId: string) => {
      return http.get(`/eventapply/${eventId}`)
    },
    postEventApply: (params: { eventId: string }) => {
      return http.post('/eventapply', params)
    },
    getMyEventReply: (eventId: string) => {
      return http.get(`/events/${eventId}/comments/mycomment`)
    },
    getEventReply: (eventsId: string, page: string, take: string) => {
      return http.get(`/events/${eventsId}/comments${getEventReplyQuery(page, take)}`)
    },
    postEventReply: (eventId: string, params: any) => {
      return http.post(`/events/${eventId}/comments/`, params)
    },
    patchEventReply: (eventId: string, eventReplyId: string, params: any) => {
      return http.patch(`/events/${eventId}/comments/${eventReplyId}`, params)
    },
    deleteEventReply: (eventId: string, eventReplyId: string) => {
      return http.del(`/events/${eventId}/comments/${eventReplyId}`)
    }
  }

  /* 초대 관련 API */
  const referralApi = {
    getReferralCode: () => {
      return http.get('/users/referral-code')
    },
    patchReferralReward: (params: ReferralRewardClaimRequest) => {
      return http.patch('/users/referral-reward/claim', params)
    }
  }
  /* 내 데이터페이지 API */
  const myDataApi = {
    getValidData: (page: number, count: number): Promise<MyDataRes> => {
      return httpV3.get(`/orders/user/${page}/${count}`)
    },
    getExpiredData: (page: number, count: number): Promise<MyDataRes> => {
      return httpV3.get(`/orders/user/expired/${page}/${count}`)
    },
    postUserMemo: (params: { orderId: string; usermemo: string }): Promise<any> => {
      return httpV3.post('/orders/usermemo', params)
    }
  }
  /* 상품 관련 API */
  const productApi = {
    getProducts: (productId: string): Promise<ProductRes> => {
      return http.get(`products/${productId}`)
    },
    getRecommendProduct: (
      productId: string,
      simType: 'esim' | 'usim',
      day: number
    ): Promise<RecommendOptionRes> => {
      return http.get(`products/${productId}/${simType}/options/recommend/${day}`)
    },
    getProductOptions: (
      productId: string,
      simType: 'esim' | 'usim',
      day: number
    ): Promise<ProductOptionsByDayRes> => {
      return http.get(`products/${productId}/${simType}/options/${day}`)
    },
    getProductCountries: (productId: string): Promise<ProductCountriesRes> => {
      return http.get(`products/${productId}/countries`)
    }
  }
  const updateToken = () => {
    return http.post('/auth', null)
  }

  const getVoucher = (orderId: string): Promise<ApiRes<GetVoucherResponse>> => {
    return http.get(`/orders/voucher/${orderId}`)
  }

  const getPointPolicy = () => {
    return http.get('/points/configures')
  }

  const getTermsById = (
    termsId: string
  ): Promise<{ success: boolean; data: { termsId: string; title: string; content: string } }> => {
    return http.get(`/terms/${termsId}`)
  }

  return {
    checkEligibilityCondition,
    getUserAccountInfo,
    signUpUser,
    loginUsimsaUser,
    loginSimpleUser,
    logoutUser,
    patchUserInformation,
    searchUserId,
    searchUserPassword,
    validateUserPasswordReset,
    patchUserPassword,
    patchLogoutUserPassword,
    getUserPurchaseAlarm,
    patchUserPurchaseAlarm,
    deleteUser,
    getAccountTerms,
    getUserMileage,
    getAvaliablePlan,
    getPlanOption,
    getAllCoupon,
    registerCoupon,
    registerCouponByUser,
    registerPublicCouponById,
    getAllPoint,
    getOrderCode,
    getOrderExpiredList,
    getOrderHistoryList,
    selectOrderInformation: getUserOrder,
    getUserOrderItem,
    getUserMultiPrdOrderItem,
    getUserOrderSim,
    createPurchaseOrder,
    createGuestPurchaseOrder,
    refundOrder,
    refundChargeOrder,
    deleteESimInfo,
    deleteMultiESimInfo,
    chargePurchaseOrder,
    getCompleteCheckinInformation,
    getGiftBoxVoucher,
    postCheckInOrder,
    getDailyUsage,
    getCategoryCode,
    getBoardAlarms,
    getBoardByNotice,
    getBoardByNoticeDetail,
    getBoardByFaq,
    getImages,
    getImageDetail,
    createReview,
    getTotalReviewList,
    getUserReviewList,
    patchUserReview,
    postSmsRequest,
    postSmsVerifying,
    sendInquiryMail,
    sendOrderMail,
    sendGiftOrderMail,
    getPartnersInfo,
    getPartnersPlanList,
    // getPartnersOptionList,
    getPartnersOption,
    enrollGuideTalk,
    getPlanRelCarrierApn,
    putExternalOrder,
    getAddon,
    getPartnersUsimPlanList,
    getDeliveryMethod,
    patchDeliveryAddress,
    getChannelEsimOption,
    // getChannelUsimOption,
    getActiveStatus,
    getRealTimeUsage,
    getUsableAddon,
    createAddon,
    getOrderUserData,
    getOptionDetail,
    getProductSearch,
    getAddonList,
    getOptionsList,
    patchUserNotification,
    postUserConsent,
    collabCouponApi,
    eventApi,
    referralApi,
    updateToken,
    getVoucher,
    myDataApi,
    productApi,
    getPointPolicy,
    getTermsById
  }
}
